import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { formatTripInviteeInput, parseTripInviteeInput } from '../../../utils/tripInvitees';
import {
  executiveCalendarListManagedExecutives,
  type ManagedExecutiveRow,
} from '../../../utils/executiveCalendarDelegatedService';
import { buildCalendarSegmentsFromTrip } from '../../../utils/peopleTravelCalendarSync';
import {
  requestTripCalendarSync,
  waitForTripCalendarSync,
} from '../../../utils/travelAgentService';
import { updatePeopleTravelTrip } from '../../../utils/peopleTravelFirestore';
import type {
  PeopleTravelEvent,
  PeopleTravelLeg,
  PeopleTravelLocation,
  PeopleTravelOption,
  PeopleTravelStop,
  PeopleTravelTrip,
} from '../../../types';
import '../../../pages/OperationsHub.css';
import './PeopleTravel.css';

type Props = {
  open: boolean;
  onClose: () => void;
  trip: PeopleTravelTrip;
  stops: PeopleTravelStop[];
  selectedOptions: PeopleTravelOption[];
  locations?: PeopleTravelLocation[];
  events?: PeopleTravelEvent[];
  legs?: PeopleTravelLeg[];
  defaultInvitees: string[];
  primaryCalendarId: string;
  onSynced: () => void;
};

const PeopleTravelCalendarSyncModal: React.FC<Props> = ({
  open,
  onClose,
  trip,
  stops,
  selectedOptions,
  locations = [],
  events = [],
  legs = [],
  defaultInvitees,
  primaryCalendarId,
  onSynced,
}) => {
  const [executives, setExecutives] = useState<ManagedExecutiveRow[]>([]);
  const [executiveUid, setExecutiveUid] = useState(trip.calendarExecutiveUid || '');
  const [inviteesText, setInviteesText] = useState(formatTripInviteeInput(defaultInvitees));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    void executiveCalendarListManagedExecutives()
      .then(res => {
        setExecutives(res.executives.filter(e => e.calendarConnected));
        if (!executiveUid && res.executives[0]?.uid) {
          setExecutiveUid(res.executives[0].uid);
        }
      })
      .catch(err => setError(err instanceof Error ? err.message : String(err)));
  }, [open, executiveUid]);

  if (!open) return null;

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const segments = buildCalendarSegmentsFromTrip({
    trip,
    stops,
    selectedOptions,
    timeZone,
    primaryCalendarId,
    locations,
    events,
    legs,
  });

  const handleSync = async () => {
    if (!executiveUid) {
      setError('Choose a managed executive with a connected calendar.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const invitees = parseTripInviteeInput(inviteesText);
      await updatePeopleTravelTrip(trip.id, {
        calendarExecutiveUid: executiveUid,
        calendarInvitees: invitees,
        calendarSyncStatus: 'pending',
      });
      await requestTripCalendarSync(trip.id, {
        executiveUid,
        invitees,
        timeZone,
        segments,
      });
      const outcome = await waitForTripCalendarSync(trip.id);
      if (outcome.status === 'failed') {
        throw new Error(outcome.error || 'Calendar sync failed');
      }
      onSynced();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ops-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="ops-modal people-travel-create-modal"
        role="dialog"
        aria-labelledby="people-travel-sync-title"
        onClick={e => e.stopPropagation()}
      >
        <div className="ops-modal-header">
          <h3 id="people-travel-sync-title">Sync to executive calendar</h3>
          <button type="button" className="ops-modal-close" aria-label="Close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="ops-modal-body people-travel-form">
          <p className="people-travel-modal-lead">
            Events are placed on a managed executive Google Calendar. Invitees receive Google
            Calendar invites on every event in the trip.
          </p>

          <div className="ops-modal-row">
            <label className="ops-modal-label" htmlFor="pt-sync-exec">
              Executive
            </label>
            <select
              id="pt-sync-exec"
              className="ops-modal-select"
              value={executiveUid}
              onChange={e => setExecutiveUid(e.target.value)}
            >
              <option value="">Select executive…</option>
              {executives.map(e => (
                <option key={e.uid} value={e.uid}>
                  {e.displayName}
                  {e.email ? ` (${e.email})` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="ops-modal-section">
            <label className="ops-modal-label" htmlFor="pt-sync-invitees">
              Invitees (emails)
            </label>
            <textarea
              id="pt-sync-invitees"
              className="ops-modal-textarea"
              rows={3}
              value={inviteesText}
              onChange={e => setInviteesText(e.target.value)}
              placeholder="one@example.com, other@example.com"
            />
          </div>

          <div className="ops-modal-section">
            <span className="ops-modal-label">Preview ({segments.length} events)</span>
            <ul className="people-travel-sync-preview">
              {segments.map(seg => (
                <li key={seg.segmentId}>
                  {seg.title} — {seg.start}
                </li>
              ))}
            </ul>
          </div>

          {error && <p className="people-travel-modal-error">{error}</p>}
        </div>

        <div className="ops-modal-footer">
          <button type="button" className="pm-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="pm-btn pm-btn-primary" onClick={() => void handleSync()} disabled={busy}>
            {busy ? 'Syncing…' : trip.calendarEventGroupId ? 'Re-sync calendar' : 'Sync to calendar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PeopleTravelCalendarSyncModal;
